
import { Link } from "react-router-dom"
import { useLocation } from "react-router-dom"
import {
  LayoutDashboard,
  Users,
  FileText,
  FolderOpen,
  Settings,
  TrendingUp,
  Presentation,
  Search,
  CreditCard,
  Activity,
  HelpCircle,
  LogOut,
} from "lucide-react"
import { cn } from "@/lib/utils"
import { IreneLogo } from "@/components/irene-logo"
import { useIntercom } from "@/hooks/use-intercom"
import { useAuth } from "@/lib/auth-context"

const navItems = [
  { href: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { href: "/dashboard/investors", label: "Investors", icon: Users },
  { href: "/dashboard/investor-search", label: "Investor Search", icon: Search },
  { href: "/dashboard/updates", label: "Update Generator", icon: FileText },
  { href: "/dashboard/deck-analyzer", label: "Deck Analyzer", icon: Presentation },
  { href: "/dashboard/deal-room", label: "Deal Room", icon: FolderOpen },
  { href: "/dashboard/analytics", label: "Analytics", icon: TrendingUp },
  { href: "/dashboard/billing", label: "Billing", icon: CreditCard },
  { href: "/dashboard/settings", label: "Settings", icon: Settings },
]

export function AppSidebar() {
  const { pathname } = useLocation()
  const { showSpace } = useIntercom()
  const { user, logout } = useAuth()

  const initials = user?.name
    ? user.name
        .split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase()
        .slice(0, 2)
    : "IR"

  const displayName = user?.company_name || user?.name || "My Company"
  const displayRole = user?.plan ? `${user.plan.charAt(0).toUpperCase()}${user.plan.slice(1)} Plan` : "Founder"

  return (
    <aside className="flex h-screen w-64 flex-col border-r border-border bg-card">
      {/* Logo */}
      <div className="flex items-center gap-3 px-6 py-5 border-b border-border">
        <IreneLogo size={36} />
        <div>
          <h1 className="text-lg font-bold text-foreground tracking-tight">
            Irene
          </h1>
          <p className="text-xs text-muted-foreground">Investor Relations AI</p>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-3 py-4">
        <ul className="flex flex-col gap-1">
          {navItems.map((item) => {
            const isActive =
              pathname === item.href ||
              (item.href !== "/dashboard" && pathname.startsWith(item.href))
            return (
              <li key={item.href}>
                <Link
                  to={item.href}
                  className={cn(
                    "flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors",
                    isActive
                      ? "bg-primary text-primary-foreground"
                      : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                  )}
                >
                  <item.icon className="h-4 w-4" />
                  {item.label}
                </Link>
              </li>
            )
          })}
        </ul>
      </nav>

      {/* Help & Support */}
      <div className="border-t border-border px-3 py-2">
        <button
          type="button"
          onClick={() => showSpace("help")}
          className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
        >
          <HelpCircle className="h-4 w-4" />
          Help & Support
        </button>
      </div>

      {/* Footer */}
      <div className="border-t border-border px-4 py-4">
        <div className="flex items-center gap-3 rounded-lg bg-muted px-3 py-2.5">
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground">
            {initials}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-foreground truncate">{displayName}</p>
            <p className="text-xs text-muted-foreground truncate">{displayRole}</p>
          </div>
          <button
            type="button"
            onClick={() => logout()}
            className="rounded-lg p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
            aria-label="Sign out"
          >
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </div>
    </aside>
  )
}
